import React, { useMemo, useCallback } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { useWordDataContext } from '../contexts/WordDataContext';
import type { BlockData, WordData } from '../config/wordOverlayConfig';

interface WordToolbarProps {
  block: BlockData;
  pageIndex: number;
}

const WordToolbar: React.FC<WordToolbarProps> = ({ block, pageIndex }) => {
  const { state, actions } = useWordDataContext();
  
  // Get selected word and its index
  const selectedWord = useMemo(() => {
    if (!state.selectedWord) return null;
    // Only return word if it matches both wordId and pageIndex
    if (state.selectedWord.pageIndex !== pageIndex) return null;
    return block.words.find(w => String(w.wordId) === String(state.selectedWord!.wordId));
  }, [block.words, state.selectedWord, pageIndex]);
  
  const wordIndex = useMemo(() => {
    if (!selectedWord) return -1;
    return block.words.findIndex(w => w.wordId === selectedWord.wordId);
  }, [block.words, selectedWord]);
  
  const canMoveUp = wordIndex > 0;
  const canMoveDown = wordIndex < block.words.length - 1;
  const totalWords = block.words.length;
  
  const blockId = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
  
  const handleMoveUp = useCallback(() => {
    if (!selectedWord) return;
    actions.reorderWordInBlock(pageIndex, blockId, selectedWord.wordId, 'up');
  }, [pageIndex, blockId, selectedWord, actions]);
  
  const handleMoveDown = useCallback(() => {
    if (!selectedWord) return;
    actions.reorderWordInBlock(pageIndex, blockId, selectedWord.wordId, 'down');
  }, [pageIndex, blockId, selectedWord, actions]);
  
  const handleDelete = useCallback(() => {
    if (!selectedWord) return;
    if (confirm(`Are you sure you want to delete "${selectedWord.text}"?`)) {
      actions.deleteWordFromBlock(pageIndex, blockId, selectedWord.wordId);
      actions.setSelectedWord(null);
    }
  }, [pageIndex, blockId, selectedWord, actions]);
  
  // Only show if a word is selected
  if (!selectedWord) return null;
  
  return (
    <div className="word-toolbar">
      <div className="word-toolbar-info">
         {wordIndex + 1}/{totalWords}
      </div>
      <button
        onClick={handleMoveUp}
        disabled={!canMoveUp}
        className={`word-toolbar-btn ${!canMoveUp ? 'disabled' : ''}`}
        title="Move up"
      >
        <ChevronUp size={12} />
      </button>
      <button
        onClick={handleMoveDown}
        disabled={!canMoveDown}
        className={`word-toolbar-btn ${!canMoveDown ? 'disabled' : ''}`}
        title="Move down"
      >
        <ChevronDown size={12} />
      </button>
      <button
        onClick={handleDelete}
        className="word-toolbar-btn word-toolbar-btn-delete"
        title="Delete word"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default WordToolbar;

